import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, MapPin, Sparkles, Users, Globe, Check } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

const COUNTRIES = [
  { code: 'US', name: 'United States', nameKo: '미국', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', nameKo: '캐나다', flag: '🇨🇦' },
  { code: 'MX', name: 'Mexico', nameKo: '멕시코', flag: '🇲🇽' },
  { code: 'GB', name: 'United Kingdom', nameKo: '영국', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', nameKo: '호주', flag: '🇦🇺' },
  { code: 'ID', name: 'Indonesia', nameKo: '인도네시아', flag: '🇮🇩' },
  { code: 'CN', name: 'China', nameKo: '중국', flag: '🇨🇳' },
  { code: 'SG', name: 'Singapore', nameKo: '싱가포르', flag: '🇸🇬' },
];

const LIFE_STAGES = [
  { id: 'student', label: 'Student', labelKo: '유학생', icon: '🎓' },
  { id: 'working', label: 'Working Professional', labelKo: '직장인', icon: '💼' },
  { id: 'relocating', label: 'Relocating Soon', labelKo: '이주 예정', icon: '✈️' },
  { id: 'permanent', label: 'Permanent Resident', labelKo: '영주권자', icon: '🏠' },
  { id: 'returning', label: 'Returning to Korea', labelKo: '귀국 예정', icon: '🇰🇷' },
  { id: 'exploring', label: 'Just Exploring', labelKo: '탐색 중', icon: '🔍' },
];

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
}

export default function WelcomeModal({ isOpen, onClose, userName }: WelcomeModalProps) {
  const [step, setStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedLifeStage, setSelectedLifeStage] = useState<string>('');
  const navigate = useNavigate();
  const { updateProfile } = useAuthStore();
  const { setSelectedCountry: setUICountry } = useUIStore();

  const handleComplete = async () => {
    // Save preferences to user profile
    if (selectedCountry || selectedLifeStage) {
      // Persist country to uiStore for auto-filtering
      if (selectedCountry) {
        setUICountry(selectedCountry);
      }
      try {
        await updateProfile({
          country_code: selectedCountry,
          life_stage: selectedLifeStage,
        });
      } catch (error) {
        console.error('Failed to save preferences:', error);
      }
    }

    // Mark onboarding as complete in localStorage
    localStorage.setItem('onboarding_complete', 'true');
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_complete', 'true');
    onClose();
  };

  const handleGoToCommunity = () => {
    handleComplete();
    navigate('/guides');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-coral-500 to-coral-600 text-white p-6 pb-12">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Welcome to KoreanHub!</h2>
              <p className="text-coral-100">환영합니다, {userName}님!</p>
            </div>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="flex gap-2 px-6 -mt-4 relative z-10">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`flex-1 h-2 rounded-full transition-colors ${
                s <= step ? 'bg-coral-500' : 'bg-gray-200 dark:bg-slate-600'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <Globe className="w-12 h-12 text-coral-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Where are you located?
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  어디에 계신가요? We'll personalize your experience.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {COUNTRIES.map((country) => (
                  <button
                    key={country.code}
                    onClick={() => setSelectedCountry(country.code)}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      selectedCountry === country.code
                        ? 'border-coral-500 bg-coral-50 dark:bg-coral-900/20'
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                    }`}
                  >
                    <span className="text-2xl">{country.flag}</span>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {country.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {country.nameKo}
                      </div>
                    </div>
                    {selectedCountry === country.code && (
                      <Check className="w-4 h-4 text-coral-500 ml-auto" />
                    )}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setStep(2)}
                className="w-full mt-4 py-3 bg-coral-500 hover:bg-coral-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setStep(2)}
                className="w-full text-gray-500 dark:text-gray-400 text-sm hover:underline"
              >
                Skip this step
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <Users className="w-12 h-12 text-coral-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  What's your situation?
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  어떤 상황이신가요? This helps us show relevant content.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {LIFE_STAGES.map((stage) => (
                  <button
                    key={stage.id}
                    onClick={() => setSelectedLifeStage(stage.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      selectedLifeStage === stage.id
                        ? 'border-coral-500 bg-coral-50 dark:bg-coral-900/20'
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                    }`}
                  >
                    <span className="text-2xl">{stage.icon}</span>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {stage.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {stage.labelKo}
                      </div>
                    </div>
                    {selectedLifeStage === stage.id && (
                      <Check className="w-4 h-4 text-coral-500 ml-auto" />
                    )}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setStep(3)}
                className="w-full mt-4 py-3 bg-coral-500 hover:bg-coral-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setStep(3)}
                className="w-full text-gray-500 dark:text-gray-400 text-sm hover:underline"
              >
                Skip this step
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-gradient-to-br from-coral-400 to-coral-600 rounded-full flex items-center justify-center mx-auto">
                <Sparkles className="w-10 h-10 text-white" />
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  You're all set!
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Start exploring the Korean community or introduce yourself.
                </p>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 text-left">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-100 dark:bg-amber-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🎁</span>
                  </div>
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      Earn +10 bonus points!
                    </p>
                    <p className="text-sm text-amber-600 dark:text-amber-300">
                      Create your first post and introduce yourself to the community.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleGoToCommunity}
                  className="w-full py-3 bg-coral-500 hover:bg-coral-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  Create My First Post
                </button>
                <button
                  onClick={handleComplete}
                  className="w-full py-3 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Explore First
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
